import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  ManageSearch as ManageSearchIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('admin_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('user_token');

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const tabLabelSx = {
  textTransform: 'none',
  fontWeight: 700,
  minHeight: 48,
};

const heroGradient = 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #38bdf8 100%)';

const toHref = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value)) return value;
  return `https://${value}`;
};

const clickableUrlSx = {
  color: 'primary.main',
  textDecoration: 'none',
  display: 'block',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

const URLManagerResponsive = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [urls, setUrls] = useState([]);
  const [filteredUrls, setFilteredUrls] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    url: '',
    keyword: '',
    userId: '',
    password: '',
    shareText: '',
  });

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/url-manager`, {
        headers: getAuthHeaders(),
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setUrls(data);
      setFilteredUrls(data);
      setError('');
    } catch (err) {
      console.error('Error fetching URLs:', err);
      setError(err.response?.data?.message || 'Failed to load URLs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrls();
  }, []);

  useEffect(() => {
    const value = searchKeyword.trim().toLowerCase();
    if (!value) {
      setFilteredUrls(urls);
      return;
    }

    setFilteredUrls(
      urls.filter((item) => {
        const keyword = String(item.keyword || '').toLowerCase();
        const url = String(item.url || '').toLowerCase();
        const userId = String(item.user_id || '').toLowerCase();
        return (
          keyword.includes(value) ||
          url.includes(value) ||
          userId.includes(value)
        );
      }),
    );
  }, [searchKeyword, urls]);

  const stats = useMemo(() => {
    const total = urls.length;
    const withPasswords = urls.filter((item) => Boolean(item.password)).length;
    const withShareText = urls.filter((item) => Boolean(item.share_text)).length;
    const filtered = filteredUrls.length;
    return [
      { label: 'Total Links', value: total, icon: <LinkIcon /> },
      { label: 'Password Items', value: withPasswords, icon: <SecurityIcon /> },
      { label: 'Share Notes', value: withShareText, icon: <ManageSearchIcon /> },
      { label: 'Visible Results', value: filtered, icon: <SearchIcon /> },
    ];
  }, [filteredUrls.length, urls]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      url: '',
      keyword: '',
      userId: '',
      password: '',
      shareText: '',
    });
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData({
      url: '',
      keyword: '',
      userId: '',
      password: '',
      shareText: '',
    });
    setOpenDialog(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setFormData({
      url: item.url || '',
      keyword: item.keyword || '',
      userId: item.user_id || '',
      password: item.password || '',
      shareText: item.share_text || '',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.url || !formData.keyword) {
      setError('URL and Keyword are required.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        url: formData.url,
        keyword: formData.keyword,
        user_id: formData.userId,
        password: formData.password,
        share_text: formData.shareText,
      };

      if (editingId) {
        await axios.put(`${API_BASE_URL}/url-manager/${editingId}`, payload, {
          headers: getAuthHeaders(),
        });
        setSuccess('URL updated successfully.');
      } else {
        await axios.post(`${API_BASE_URL}/url-manager`, payload, {
          headers: getAuthHeaders(),
        });
        setSuccess('URL added successfully.');
      }

      resetDialog();
      await fetchUrls();
    } catch (err) {
      console.error('Error saving URL:', err);
      setError(err.response?.data?.message || 'Failed to save URL.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this URL entry?');
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/url-manager/${id}`, {
        headers: getAuthHeaders(),
      });
      setSuccess('URL deleted successfully.');
      await fetchUrls();
    } catch (err) {
      console.error('Error deleting URL:', err);
      setError(err.response?.data?.message || 'Failed to delete URL.');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setSuccess(`${label} to clipboard.`);
    } catch (err) {
      console.error('Clipboard error:', err);
      setError('Unable to copy to clipboard.');
    }
  };

  const getVisiblePassword = (item) => {
    return showPassword[item.id] ? item.password : '••••••••';
  };

  const renderUrlCard = (item) => (
    <Card
      key={item.id}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.10)',
        },
      }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 44,
              height: 44,
              mt: 0.25,
            }}
          >
            <LinkIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} noWrap>
                  {item.keyword}
                </Typography>
                <Typography
                  component="a"
                  href={toHref(item.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  noWrap
                  sx={clickableUrlSx}
                >
                  {item.url}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={item.user_id ? 'Has User ID' : 'No User ID'} variant="outlined" />
                <Chip size="small" label={item.password ? 'Password Saved' : 'No Password'} color={item.password ? 'success' : 'default'} variant={item.password ? 'filled' : 'outlined'} />
              </Stack>
            </Stack>

            {item.share_text && (
              <Typography variant="body2" sx={{ mt: 1.2 }} color="text.secondary">
                {item.share_text}
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {item.user_id && <Chip size="small" label={`User: ${item.user_id}`} />}
                {item.password && <Chip size="small" label={getVisiblePassword(item)} variant="outlined" />}
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="Open URL">
                  <IconButton size="small" onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy URL">
                  <IconButton size="small" onClick={() => copyToClipboard(item.url, 'URL copied')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {item.password && (
                  <Tooltip title={showPassword[item.id] ? 'Hide password' : 'Show password'}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setShowPassword((prev) => ({
                          ...prev,
                          [item.id]: !prev[item.id],
                        }))
                      }
                    >
                      {showPassword[item.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 4,
          color: '#fff',
          background: heroGradient,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 34%), radial-gradient(circle at bottom left, rgba(56,189,248,0.22), transparent 28%)',
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ position: 'relative' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box sx={{ maxWidth: 720 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}>
                  <LinkIcon />
                </Avatar>
                <Typography variant="overline" sx={{ letterSpacing: 1.1, opacity: 0.9 }}>
                  URL Manager
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={900} sx={{ lineHeight: 1.1, mb: 1 }}>
                Search and manage important URLs in one clean workspace.
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 680 }}>
                Professional admin view for quick lookup, secure credential handling, and fast URL maintenance.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={user?.role || 'admin'} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
              <Chip label={`${urls.length} total links`} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {(error || success) && (
        <Stack spacing={1.25} sx={{ mb: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
        </Stack>
      )}

      <Grid container spacing={2.25} sx={{ mb: 2.5 }}>
        {stats.map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 40, height: 40 }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={900} lineHeight={1}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 45px rgba(15, 23, 42, 0.08)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 54 }}
          >
            <Tab label="Search URLs" sx={tabLabelSx} />
            <Tab label="Manage URLs" sx={tabLabelSx} />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {activeTab === 0 && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                placeholder="Search by keyword, URL, or user ID"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: '#fff',
                  },
                }}
              />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : filteredUrls.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {searchKeyword ? 'No matching URLs found' : 'No URLs available yet'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchKeyword
                      ? 'Try a different keyword, URL, or user ID.'
                      : 'Add your first URL from the Manage tab.'}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {filteredUrls.map(renderUrlCard)}
                </Stack>
              )}
            </Stack>
          )}

          {activeTab === 1 && (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    URL Library
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add, edit, and delete your saved links.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  sx={{
                    borderRadius: 999,
                    px: 2.25,
                    py: 1.1,
                    textTransform: 'none',
                    fontWeight: 800,
                    boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
                  }}
                >
                  Add New URL
                </Button>
              </Stack>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : urls.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    No URLs added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create your first entry to start managing links.
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} sx={{ borderRadius: 999, textTransform: 'none' }}>
                    Add New URL
                  </Button>
                </Paper>
              ) : (
                <>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Stack spacing={1.5}>
                      {urls.map((item) => (
                        <Card key={item.id} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                          <CardContent sx={{ p: 2 }}>
                            <Stack spacing={1.25}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle1" fontWeight={800} noWrap>
                                    {item.keyword}
                                  </Typography>
                                  <Typography
                                    component="a"
                                    href={toHref(item.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    noWrap
                                    sx={clickableUrlSx}
                                  >
                                    {item.url}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton size="small" onClick={() => openEditDialog(item)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {item.user_id && <Chip size="small" label={`User: ${item.user_id}`} />}
                                {item.password && <Chip size="small" label={showPassword[item.id] ? item.password : 'Password saved'} variant="outlined" />}
                              </Stack>
                              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {item.share_text ? 'Has share text' : 'No share text'}
                                </Typography>
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton size="small" onClick={() => copyToClipboard(item.url, 'URL copied')}>
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                  {item.password && (
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setShowPassword((prev) => ({
                                          ...prev,
                                          [item.id]: !prev[item.id],
                                        }))
                                      }
                                    >
                                      {showPassword[item.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>

                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 3 }}>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                          <Box component="thead" sx={{ bgcolor: 'grey.50' }}>
                            <Box component="tr">
                              <Box component="th" sx={{ textAlign: 'left', p: 2, fontWeight: 800 }}>Keyword</Box>
                              <Box component="th" sx={{ textAlign: 'left', p: 2, fontWeight: 800 }}>URL</Box>
                              <Box component="th" sx={{ textAlign: 'left', p: 2, fontWeight: 800 }}>User ID</Box>
                              <Box component="th" sx={{ textAlign: 'left', p: 2, fontWeight: 800 }}>Password</Box>
                              <Box component="th" sx={{ textAlign: 'right', p: 2, fontWeight: 800 }}>Actions</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {urls.map((item) => (
                              <Box component="tr" key={item.id} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box component="td" sx={{ p: 2, verticalAlign: 'top' }}>
                                  <Typography fontWeight={700}>{item.keyword}</Typography>
                                  {item.share_text && (
                                    <Typography variant="caption" color="text.secondary">
                                      {item.share_text}
                                    </Typography>
                                  )}
                                </Box>
                                <Box component="td" sx={{ p: 2, verticalAlign: 'top' }}>
                                  <Typography
                                    component="a"
                                    href={toHref(item.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    sx={{ ...clickableUrlSx, wordBreak: 'break-all' }}
                                  >
                                    {item.url}
                                  </Typography>
                                </Box>
                                <Box component="td" sx={{ p: 2, verticalAlign: 'top' }}>
                                  {item.user_id ? <Chip size="small" label={item.user_id} /> : '-'}
                                </Box>
                                <Box component="td" sx={{ p: 2, verticalAlign: 'top' }}>
                                  {item.password ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {showPassword[item.id] ? item.password : '••••••••'}
                                      </Typography>
                                      <IconButton size="small" onClick={() => setShowPassword((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}>
                                        {showPassword[item.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                      </IconButton>
                                      <IconButton size="small" onClick={() => copyToClipboard(item.password, 'Password copied')}>
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    '-'
                                  )}
                                </Box>
                                <Box component="td" sx={{ p: 2, verticalAlign: 'top', textAlign: 'right' }}>
                                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                    <Tooltip title="Edit">
                                      <IconButton color="primary" size="small" onClick={() => openEditDialog(item)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton color="error" size="small" onClick={() => handleDelete(item.id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </Box>
      </Card>

      <Dialog open={openDialog} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <LinkIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={900} lineHeight={1.1}>
                {editingId ? 'Edit URL' : 'Add New URL'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Keep keywords short and URLs readable for quick lookup.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Keyword"
                name="keyword"
                value={formData.keyword}
                onChange={handleInputChange}
                placeholder="e.g. KNOW NIDHI ID"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="User ID"
                name="userId"
                value={formData.userId}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                required
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Share Text"
                name="shareText"
                value={formData.shareText}
                onChange={handleInputChange}
                placeholder="Optional note to display"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={resetDialog} sx={{ textTransform: 'none', borderRadius: 999 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 800 }}
          >
            {editingId ? 'Update URL' : 'Save URL'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default URLManagerResponsive;
